import React, { useState, useEffect, useMemo } from "react";
import styles from "./IncidentsPage.module.css";

import Card from "./Card";
import IncidentsList from "./IncidentsList";
import PaginationControls from "shared/PaginationControls";
import UpcomingActionsList from "./UpcomingActionsList";
import PastActionsList from "./PastActionsList";
import CreateNewReportModal from "./CreateNewReportModal";
import FilterMenu from "./FilterMenu";
import {
  useAddIncident,
  useIncidents,
  useClearReminder,
  useClearSuspensionReminder,
} from "./useIncidents";

import getPaginatedList from "./utils/getPaginatedList";

export function IncidentsPage() {
  const actionsPerPage = 8;

  const { data, status, refetch } = useIncidents("incidents");
  const [pastData, setPastData] = useState([]);
  const [upcomingData, setUpcomingData] = useState([]);
  const [selectOptions, setSelectOptions] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState(null);

  const [createNewReportModalOpen, setCreateNewReportModalOpen] =
    useState(false);

  const [currentUpcomingActionList, setCurrentUpcomingActionList] = useState(
    []
  );
  const [upcomingActionPage, setUpcomingActionPage] = useState(1);
  const upcomingActionMaxPages = useMemo(
    () => (upcomingData ? Math.ceil(upcomingData.length / actionsPerPage) : 1),
    [upcomingData]
  );

  const [currentPastActionsList, setCurrentPastActionsList] = useState([]);
  const [pastActionsPage, setPastActionsPage] = useState(1);
  const pastActionsMaxPages = useMemo(
    () => (pastData ? Math.ceil(pastData.length / actionsPerPage) : 1),
    [pastData]
  );

  useEffect(() => {
    if (data) {
      const pastData = data.flatMap((item) => {
        const actionItems = item.historyOfActions.map((action) => ({
          ...item,
          historyOfActions: [action],
          reminderDate: new Date(action.createdAt),
        }));

        return [...actionItems];
      });

      const filteredData = pastData.filter((item) => {
        if (!item.status || item.status === "Closed") {
          return item;
        }
      });
      const sortedData = filteredData.sort(
        (a, b) => b.reminderDate - a.reminderDate
      );

      setPastData(sortedData);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const upcomingData = data.flatMap((item) => {
        const activeReminders = item.reminders.filter(
          (reminder) => reminder.isActive !== false
        );

        const reminderItems = activeReminders.map((reminder) => ({
          ...item,
          reminders: [reminder],
          type: "reminder",
          reason: reminder.reason,
          reminderId: reminder._id,
          reminderDate: new Date(reminder.reminderDate),
        }));

        const pendingSuspensionItem =
          item.pendingSuspension &&
          item.pendingSuspension.platforms &&
          item.pendingSuspension.platforms.length > 0 &&
          item.pendingSuspension.isActive !== false
            ? {
                ...item,
                type: "pendingSuspension",
                reason: "Suspension",
                reminderDate: new Date(item.pendingSuspension.reminderDate),
              }
            : null;

        return [...reminderItems, pendingSuspensionItem];
      });

      const filteredData = upcomingData.filter((item) => item !== null);
      const sortedData = filteredData.sort(
        (a, b) => a.reminderDate - b.reminderDate
      );

      setUpcomingData(sortedData);
    }
  }, [data]);

  useEffect(() => {
    setCurrentUpcomingActionList(
      getPaginatedList(upcomingData, actionsPerPage, upcomingActionPage)
    );
  }, [upcomingData, upcomingActionPage]);

  useEffect(() => {
    setCurrentPastActionsList(
      getPaginatedList(pastData, actionsPerPage, pastActionsPage)
    );
  }, [pastData, pastActionsPage]);

  useEffect(() => {
    if (data) {
      setFilteredIncidents(data);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const statusOptions = data
        ? [
            "Select",
            ...Array.from(
              new Set(data.map((incident) => incident.status))
            ).filter((status) => status && status.trim() !== ""),
          ]
        : [];

      const typeOptions = data
        ? [
            "Select",
            ...Array.from(
              new Set(data.map((incident) => incident.problemType))
            ).filter((type) => type && type.trim() !== ""),
          ]
        : [];

      const severityOptions = [
        "Select",
        "Level 1",
        "Level 2",
        "Level 3",
        "Level 4",
        "Level 5",
      ];

      const caseOwnerOptions = data
        ? [
            "Select",
            ...Array.from(
              new Set(data.map((incident) => incident.caseOwner))
            ).filter((type) => type && type.trim() !== ""),
          ]
        : [];

      setSelectOptions({
        status: statusOptions,
        type: typeOptions,
        severity: severityOptions,
        caseOwner: caseOwnerOptions,
      });
    }
  }, [data]);

  const handleFiltering = (filter) => {
    const startDate = filter.startDate ? new Date(filter.startDate) : null;
    const endDate = filter.endDate ? new Date(filter.endDate) : null;

    const filteredData = data.filter((incident) => {
      const createdAt = new Date(incident.createdAt);

      const isWithinDateRange =
        (!startDate || startDate <= createdAt) &&
        (!endDate || createdAt <= endDate);

      const searchQuery = filter.search
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "");

      const isMatched =
        (incident.caseOwner &&
          incident.caseOwner
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(searchQuery)) ||
        (incident.complaintAgainst.iboNumber &&
          incident.complaintAgainst.iboNumber
            .toString()
            .includes(searchQuery)) ||
        (incident.problemType &&
          incident.problemType
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(searchQuery)) ||
        (incident.status &&
          incident.status
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(searchQuery)) ||
        (incident.id && incident.id.toLowerCase().includes(searchQuery));

      return (
        isWithinDateRange &&
        (filter.status === "Select" || incident.status === filter.status) &&
        (filter.type === "Select" || incident.problemType === filter.type) &&
        (filter.severity === "Select" ||
          incident.severity === filter.severity) &&
        (filter.owner === "Select" || incident.caseOwner === filter.owner) &&
        (filter.search === "" || isMatched)
      );
    });
    setFilteredIncidents(filteredData);
  };

  //const addIncident = useAddIncident();

  const { addIncident } = useAddIncident();

  async function handleOnCreateNewReport(incident) {
    const json = { incident };
    addIncident.mutateAsync(json).then(() => {
      refetch();
    });
    setCreateNewReportModalOpen(false);
    // await addIncident(report);
    // setCreateNewReportModalOpen(false);
  }

  const clearReminderPut = useClearReminder();
  const clearSuspensionReminderPut = useClearSuspensionReminder();

  async function clearReminder(id, reminderId) {
    if (reminderId) {
      const reminderObject = {
        id: id,
        reminderId: reminderId,
        isActive: false,
      };
      clearReminderPut.mutateAsync(reminderObject).then(() => {
        refetch();
      });
    } else {
      const reminderObject = {
        id: id,
        isActive: false,
      };
      clearSuspensionReminderPut.mutateAsync(reminderObject).then(() => {
        refetch();
      });
    }
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>INCIDENTS</h1>
      <div className={styles.filterAndCreateNewContainer}>
        <div className={styles.filterByText}>Filter By</div>
        <div className={styles.filterMenuContainer}>
          <FilterMenu
            filterFunction={handleFiltering}
            incidents={data}
            selectOptions={selectOptions}
          />
        </div>
        <div
          className={styles.button}
          onClick={() => setCreateNewReportModalOpen(true)}
        >
          CREATE NEW
        </div>
      </div>
      <Card
        title="INCIDENT OVERVIEW"
        children={
          <div className={styles.incidentOverviewContent}>
            <div className={styles.usersDiv}>
              {status === "success" && filteredIncidents ? (
                <>
                  <IncidentsList
                    incidents={filteredIncidents}
                    resultsPerPage={12}
                  />
                </>
              ) : status === "loading" ? (
                <div className={styles.loadMessage}>Loading data...</div>
              ) : (
                <div className={styles.loadMessage}>Error fetching data</div>
              )}
            </div>
          </div>
        }
      />

      <div className={styles.actionsGrid}>
        <Card
          title="UPCOMING ACTION"
          className={styles.actionsGridItem}
          children={
            <div className={styles.incidentOverviewContent}>
              <UpcomingActionsList
                upcomingActions={currentUpcomingActionList}
                clearReminder={clearReminder}
              />
              <div className={styles.paginationControls}>
                <PaginationControls
                  page={upcomingActionPage}
                  lastPage={upcomingActionMaxPages}
                  onPageChange={(page) => setUpcomingActionPage(page)}
                  color={"#1F3BD8"}
                  disabled={!currentUpcomingActionList}
                  disableNext={upcomingActionPage >= upcomingActionMaxPages}
                />
              </div>
            </div>
          }
        />

        <Card
          title="PAST ACTIONS"
          className={styles.actionsGridItem}
          children={
            <div className={styles.incidentOverviewContent}>
              <PastActionsList pastActions={currentPastActionsList} />
              <div className={styles.paginationControls}>
                <PaginationControls
                  page={pastActionsPage}
                  lastPage={pastActionsMaxPages}
                  onPageChange={(page) => setPastActionsPage(page)}
                  color={"#1F3BD8"}
                  disabled={!currentPastActionsList}
                  disableNext={pastActionsPage >= pastActionsMaxPages}
                />
              </div>
            </div>
          }
        />
      </div>
      <CreateNewReportModal
        isOpen={createNewReportModalOpen}
        onCreate={handleOnCreateNewReport}
        onClose={() => setCreateNewReportModalOpen(false)}
      />
    </div>
  );
}
